import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { fetchProcessedBankStatements, fetchCaseCheckImages, fetchCaseFiles, fetchCaseEntities } from '../../api/cases';

interface Stats {
  files: number;
  bankStatements: number;
  transactions: number;
  checkImages: number;
  entities: number;
  pendingClassification: number;
  processingExtraction: number;
}

interface Props {
  case_id: string;
}

function useAnimatedCount(target: number | null) {
  const [display, setDisplay] = useState<number | null>(target);
  const rafRef = useRef<number>();
  const prevRef = useRef<number | null>(target);

  useEffect(() => {
    if (target === null) { setDisplay(null); return; }
    const from = prevRef.current ?? target;
    prevRef.current = target;
    if (from === target) { setDisplay(target); return; }
    const start = performance.now();
    const duration = 600;
    const tick = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(from + (target - from) * eased));
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target]);

  return display;
}

const StatCard: React.FC<{ label: string; value: number | null; pending?: boolean }> = ({ label, value, pending }) => {
  const animated = useAnimatedCount(value);
  return (
    <div style={{
      flex: '1 1 0',
      minWidth: '110px',
      padding: '10px 14px',
      borderLeft: `3px solid ${pending ? '#f59e0b' : '#d1d5db'}`,
      transition: 'border-color 0.3s',
    }}>
      <div style={{
        fontSize: '1.45rem',
        fontWeight: 600,
        color: '#111827',
        letterSpacing: '-0.02em',
        lineHeight: 1,
        fontVariantNumeric: 'tabular-nums',
      }}>
        {animated === null ? '—' : animated.toLocaleString()}
        {pending && (
          <span
            title="Processing in progress"
            style={{
              display: 'inline-block',
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: '#f59e0b',
              marginLeft: 5,
              verticalAlign: 'middle',
              animation: 'pulse-dot 1.4s ease-in-out infinite',
            }}
          />
        )}
      </div>
      <div style={{
        fontSize: '0.68rem',
        color: '#9ca3af',
        textTransform: 'uppercase',
        letterSpacing: '0.07em',
        marginTop: 4,
        fontWeight: 500,
      }}>
        {label}
      </div>
    </div>
  );
};

async function loadStats(case_id: string, token: string): Promise<Stats> {
  const [statementsRes, checkImagesRes, filesRes, entitiesRes] = await Promise.allSettled([
    fetchProcessedBankStatements(case_id, token),
    fetchCaseCheckImages(case_id, token),
    fetchCaseFiles(case_id, token),
    fetchCaseEntities(case_id, token),
  ]);

  let files = 0, bankStatements = 0, transactions = 0, checkImages = 0, entities = 0;
  let pendingClassification = 0, processingExtraction = 0;

  if (statementsRes.status === 'fulfilled') {
    const statements = statementsRes.value.data.statements || [];
    bankStatements = statements.length;
    transactions = statements.reduce((sum: number, st: any) => sum + (st.transactions?.length || 0), 0);
  }
  if (checkImagesRes.status === 'fulfilled') {
    checkImages = (checkImagesRes.value.data.check_images || []).length;
  }
  if (filesRes.status === 'fulfilled') {
    const allFiles: any[] = filesRes.value.data.files || [];
    files = allFiles.length;
    pendingClassification = allFiles.filter(
      f => !f.classification_status || f.classification_status === 'pending' || f.classification_status === 'processing'
    ).length;
    processingExtraction = allFiles.filter(
      f => f.processing_status === 'processing'
        || f.bank_processing_status === 'processing'
        || f.gl_processing_status === 'processing'
    ).length;
  }
  if (entitiesRes.status === 'fulfilled') {
    const ed = entitiesRes.value;
    entities = ed.total ?? ed.entities?.length ?? 0;
  }

  return { files, bankStatements, transactions, checkImages, entities, pendingClassification, processingExtraction };
}

const CaseStats: React.FC<Props> = ({ case_id }) => {
  const { getAccessToken } = useAuth();
  const [stats, setStats] = useState<Stats | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();

  const refresh = async () => {
    const token = await getAccessToken();
    setStats(await loadStats(case_id, token));
  };

  useEffect(() => { refresh(); }, [case_id]);

  useEffect(() => {
    if (!stats) return;
    const needsPoll = stats.pendingClassification > 0 || stats.processingExtraction > 0;
    if (needsPoll) intervalRef.current = setInterval(refresh, 5000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [stats?.pendingClassification, stats?.processingExtraction]);

  if (!stats) return null;

  const hasAnyData = stats.files > 0 || stats.bankStatements > 0 || stats.checkImages > 0 || stats.entities > 0;
  if (!hasAnyData) return null;

  const hasPending = stats.pendingClassification > 0 || stats.processingExtraction > 0;

  return (
    <div className="mb-4">
      <style>{`@keyframes pulse-dot { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.4;transform:scale(1.5)} }`}</style>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <span style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#9ca3af' }}>
          Extracted Data
        </span>
        {hasPending && (
          <span style={{ fontSize: '0.72rem', color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#f59e0b', animation: 'pulse-dot 1.4s ease-in-out infinite' }} />
            {stats.pendingClassification > 0
              ? `${stats.pendingClassification} file${stats.pendingClassification !== 1 ? 's' : ''} classifying`
              : `${stats.processingExtraction} file${stats.processingExtraction !== 1 ? 's' : ''} extracting`}
          </span>
        )}
      </div>
      <div className="d-flex gap-4 flex-wrap">
        <StatCard label="Files" value={stats.files} pending={stats.pendingClassification > 0} />
        <StatCard label="Bank Statements" value={stats.bankStatements} pending={stats.processingExtraction > 0} />
        <StatCard label="Transactions" value={stats.transactions} pending={stats.processingExtraction > 0} />
        <StatCard label="Check Images" value={stats.checkImages} pending={stats.processingExtraction > 0} />
        <StatCard label="Entities" value={stats.entities} />
      </div>
    </div>
  );
};

export default CaseStats;
